<!--
	@component Logo
	Ameta brand logo SVG component. Renders the full "ameta" wordmark.

	@prop {string} [class] - Additional CSS classes to apply to the SVG element.
	@prop {'sm' | 'md' | 'lg'} [size='md'] - Predefined size: sm (h-6), md (h-7), lg (h-8).

	@example
	```svelte
	<Logo size="lg" class="text-white" />
	```
-->
<script lang="ts">
	/**
	 * Props for the Logo component.
	 * @typedef {Object} Props
	 * @property {string} [class] - Additional CSS classes
	 * @property {'sm' | 'md' | 'lg'} [size] - Predefined size variant
	 */
	interface Props {
		class?: string;
		size?: 'sm' | 'md' | 'lg';
	}

	let { class: className = '', size = 'md' }: Props = $props();

	const sizeClasses = {
		sm: 'h-6',
		md: 'h-7',
		lg: 'h-8'
	};
</script>

<svg
	aria-hidden="true"
	viewBox="0 0 153 34"
	class="{sizeClasses[size]} w-auto {className}"
	style="shape-rendering: geometricPrecision;"
>
	<!-- Letter 'a' (first) -->
	<path
		d="M14.86,5.67C7.72,5.26 1.41,10.05 0.24,16.78C-1.04,24.12 2.99,31.02 9.97,32.72C13.73,33.63 17.64,33.71 20.6,30.38C20.6,30.38 20.65,30.33 20.67,30.31C20.7,30.28 20.76,30.29 20.85,30.3C20.91,30.3 20.99,30.32 21.06,30.33L21.42,32.62L27.84,32.62C27.84,27.86 27.97,23.23 27.81,18.62C27.57,11.66 21.89,6.1 14.85,5.69L14.86,5.67ZM13.92,26.72C9.8,26.72 6.72,23.59 6.77,19.46C6.82,15.38 9.84,12.34 13.87,12.32C18.05,12.3 21.06,15.3 21.08,19.5C21.09,23.71 18.11,26.72 13.93,26.72L13.92,26.72Z"
		fill="currentColor"
	/>
	<!-- Letter 'e' -->
	<path
		d="M96.91,22.35L103.72,22.35C103.93,21.38 104.05,20.38 104.05,19.35C104.05,16.01 102.86,12.94 100.89,10.56C99.44,8.74 97.52,7.33 95.27,6.5C93.71,5.89 92.02,5.55 90.24,5.55C82.61,5.55 76.43,11.73 76.43,19.36C76.43,26.99 82.61,33.17 90.24,33.17C95.59,33.17 100.22,30.13 102.51,25.68L95.22,25.68C93.87,27.11 91.89,27.79 89.61,27.55C86.39,27.2 84,25 83.99,22.36L96.9,22.36L96.91,22.35ZM95.96,14.57C96.18,14.91 96.37,15.28 96.53,15.68C96.69,16.08 96.82,16.5 96.92,16.96L84.01,16.96C84.01,16.81 84.01,16.66 84.04,16.51C84.07,16.36 84.08,16.21 84.11,16.07C84.12,16.01 84.14,15.95 84.15,15.9C84.18,15.77 84.22,15.64 84.27,15.51C84.41,15.13 84.6,14.76 84.83,14.42C84.96,14.23 85.1,14.05 85.26,13.87C85.48,13.62 85.74,13.39 86.01,13.18C86.28,12.97 86.58,12.77 86.9,12.6C87.38,12.34 87.9,12.14 88.46,11.99C88.65,11.94 88.84,11.9 89.03,11.86C89.22,11.82 89.42,11.8 89.62,11.77C90.19,11.71 90.74,11.7 91.26,11.76C91.79,11.81 92.29,11.92 92.76,12.08C92.95,12.14 93.13,12.21 93.31,12.29C93.67,12.45 94.01,12.64 94.33,12.87C94.65,13.09 94.94,13.35 95.21,13.63C95.48,13.91 95.73,14.23 95.94,14.57L95.96,14.57Z"
		fill="currentColor"
	/>
	<!-- Letter 'a' (last) -->
	<path
		d="M152.31,18.5C152.07,11.54 146.39,5.98 139.35,5.57C132.21,5.16 125.9,9.95 124.73,16.68C123.45,24.02 127.48,30.92 134.46,32.62C138.22,33.53 142.13,33.61 145.09,30.28C145.09,30.28 145.14,30.23 145.16,30.21C145.19,30.18 145.25,30.19 145.34,30.2C145.4,30.2 145.48,30.22 145.55,30.23L145.91,32.52L152.33,32.52C152.33,27.76 152.46,23.13 152.3,18.52L152.31,18.5ZM138.42,26.61C134.3,26.61 131.22,23.48 131.27,19.35C131.32,15.27 134.34,12.23 138.37,12.21C142.55,12.19 145.56,15.19 145.58,19.39C145.59,23.6 142.61,26.61 138.43,26.61L138.42,26.61Z"
		fill="currentColor"
	/>
	<!-- Letter 't' -->
	<path
		d="M121.62,26.22C120.88,26.06 120.15,25.86 119.57,25.67C119.12,25.56 118.7,25.41 118.31,25.24C118.28,25.24 118.27,25.22 118.27,25.22C116.42,24.38 115.23,22.87 115.03,20.82C114.78,18.27 114.98,15.68 114.98,12.89L120.67,12.89L120.67,7.02L114.97,6.32L114.84,6.3L114.84,0L108.16,0L108.16,19.35C108.19,19.97 108.22,20.59 108.27,21.22C108.31,21.84 108.41,22.45 108.55,23.04C109.94,28.84 115.86,33.05 121.65,32.48L121.65,26.24C121.65,26.24 121.64,26.24 121.63,26.23L121.62,26.22Z"
		fill="currentColor"
	/>
	<!-- Letter 'm' -->
	<path
		d="M72,15.23L72,15.15C71.9,13.14 70.92,7.43 63.12,7.03L63.12,7.05C62.98,7.05 62.83,7.03 62.67,7.03C59.38,7.03 56.43,8.82 55.11,11.39C53.79,13.96 55.06,11.5 55,11.5L54.98,11.5C54.9,11.5 54.86,11.39 54.86,11.39C53.59,8.84 50.74,7.04 47.6,7.04C44.77,7.04 42.29,8.47 40.9,10.63L40.58,10.63C40.51,10.63 40.49,10.58 40.49,10.55L40.18,8.27L33.86,8.27L33.86,31.43L33.84,31.43L33.84,32.65L40.52,32.65L40.52,31.43C40.52,28.16 40.49,19.44 40.49,19.2C40.49,19.2 40.45,18.73 40.6,18.09C40.83,17.1 41.5,15.69 43.43,14.97C43.84,14.82 44.31,14.69 44.85,14.61C45.23,14.56 45.63,14.54 46.08,14.56C47.46,14.61 48.74,15.42 49.29,16.69C49.44,17.04 49.56,17.46 49.64,17.95L49.64,32.65L56.28,32.65L56.28,18C56.48,17.29 56.91,16.38 57.84,15.66C57.86,15.65 57.87,15.63 57.89,15.62C57.91,15.6 57.93,15.59 57.96,15.57C58.3,15.32 58.7,15.09 59.18,14.91L59.22,14.91C59.88,14.67 60.69,14.54 61.68,14.57C63.06,14.62 64.34,15.43 64.89,16.7C65.16,17.32 65.33,18.12 65.34,19.15L65.34,21.82L65.35,31.46L65.35,32.68L72.02,32.68L72.02,15.85C72.02,15.85 72.04,15.62 72.02,15.23L72,15.23Z"
		fill="currentColor"
	/>
</svg>
